import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';

const KPIInputModal = ({ show, onHide, onSubmit, kpiParameters = [], loading = false }) => {
  const [kpiValues, setKpiValues] = useState({});
  const [errors, setErrors] = useState({});

  const handleInputChange = (paramName, value) => {
    setKpiValues(prev => ({
      ...prev,
      [paramName]: parseFloat(value) || 0
    }));
    
    // Clear error when user starts typing
    if (errors[paramName]) {
      setErrors(prev => ({
        ...prev,
        [paramName]: null
      }));
    }
  };

  const validateInputs = () => {
    const newErrors = {};
    
    kpiParameters.forEach(param => {
      const value = kpiValues[param.name];
      
      if (value === undefined || value === null || isNaN(value)) {
        newErrors[param.name] = `${param.label} is required`;
      } else if (value < param.min || value > param.max) {
        newErrors[param.name] = `${param.label} must be between ${param.min} and ${param.max}`;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateInputs()) {
      onSubmit(kpiValues);
    }
  };

  const handleReset = () => {
    const defaultValues = {};
    kpiParameters.forEach(param => {
      defaultValues[param.name] = param.default;
    });
    setKpiValues(defaultValues);
    setErrors({});
  };

  const renderKPIInput = (param) => {
    const value = kpiValues[param.name] ?? param.default;
    const hasError = !!errors[param.name];

    return (
      <Form.Group key={param.name} className="mb-3">
        <Form.Label>
          <strong>{param.label}</strong>
          <small className="text-muted d-block">{param.description}</small>
        </Form.Label>
        <Form.Control
          type="number"
          value={value}
          onChange={(e) => handleInputChange(param.name, e.target.value)}
          min={param.min}
          max={param.max}
          step={param.step}
          isInvalid={hasError}
          disabled={loading}
        />
        {hasError && (
          <Form.Control.Feedback type="invalid">
            {errors[param.name]}
          </Form.Control.Feedback>
        )}
        <Form.Text className="text-muted">
          Range: {param.min} - {param.max} (Default: {param.default})
        </Form.Text>
      </Form.Group>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton={!loading}>
        <Modal.Title>
          🔧 KPI Parameter Input Required
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <Alert variant="info" className="mb-4">
          <Alert.Heading>📊 Critical Process Parameters</Alert.Heading>
          <p className="mb-0">
            Please provide the following KPI values for accurate bioprocess calculations. 
            These parameters directly affect engineering calculations like tip speed, power input, and mass transfer.
          </p>
        </Alert>

        <Form>
          {kpiParameters.map(renderKPIInput)}
        </Form>

        {Object.keys(errors).length > 0 && (
          <Alert variant="danger" className="mt-3">
            <strong>Please correct the following errors:</strong>
            <ul className="mb-0 mt-2">
              {Object.values(errors).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handleReset}
          disabled={loading}
        >
          Reset to Defaults
        </Button>
        <Button 
          variant="outline-secondary" 
          onClick={onHide}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Processing...
            </>
          ) : (
            'Continue with Calculations'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default KPIInputModal;
